import { useSyncExternalStore, useCallback } from 'react';
import { useOrgKey } from '@reflex/ui/client/context';
import type { RepoSelectionHistory, RepoSelectionStats } from '../shared/repo-ranking.js';

const STORAGE_KEY_PREFIX = 'repo-selection-history';

/**
 * How many per-repo pick timestamps are kept. The frequency term saturates
 * around a handful of recent picks (`FREQUENCY_SATURATION`), so anything past
 * this window cannot change a score — it would only grow the stored blob. The
 * all-time `count` is kept separately and is never truncated, so trimming here
 * costs the popularity signal nothing.
 */
const MAX_SELECTIONS_PER_REPO = 25;

/**
 * How many repos are tracked at once. Well past any one person's working set;
 * the cap exists so a long-lived browser profile cannot grow this entry
 * without bound. Eviction drops the least-recently-picked repo, which is the
 * one whose score has already decayed closest to zero.
 */
const MAX_TRACKED_REPOS = 200;

/**
 * Pick history for the repo pickers, stored per organization.
 *
 * Per-org for the same reason favorites are (see {@link import('./useFavoriteRepos.js')}):
 * each org has its own GitHub App installations, so a browser-global history
 * would let one tenant's private repo names influence — and, through ordering,
 * be inferred from — another tenant's picker. It also keeps the ranking honest:
 * how often you pick a repo in org A says nothing about org B.
 */
export function repoSelectionHistoryStorageKey(orgKey: string): string {
  return `${STORAGE_KEY_PREFIX}:${orgKey}`;
}

const EMPTY: RepoSelectionHistory = {};

/** One repo's persisted entry, validated field by field. This is
 * localStorage — an older build, a hand-edited entry, or a partially written
 * blob can all put anything here, and a malformed number would silently
 * poison the ordering rather than fail loudly. */
function parseStats(raw: unknown): RepoSelectionStats | null {
  if (!raw || typeof raw !== 'object') return null;
  const { count, lastSelectedAt, recentSelections } = raw as Record<string, unknown>;
  if (typeof count !== 'number' || !Number.isFinite(count) || count <= 0) return null;
  if (typeof lastSelectedAt !== 'number' || !Number.isFinite(lastSelectedAt)) return null;
  const selections = Array.isArray(recentSelections)
    ? recentSelections.filter((at): at is number => typeof at === 'number' && Number.isFinite(at))
    : [];
  return { count, lastSelectedAt, recentSelections: selections.slice(0, MAX_SELECTIONS_PER_REPO) };
}

function readStore(key: string): RepoSelectionHistory {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return EMPTY;
    const history: Record<string, RepoSelectionStats> = {};
    for (const [fullName, value] of Object.entries(parsed as Record<string, unknown>)) {
      const stats = parseStats(value);
      if (stats) history[fullName] = stats;
    }
    return history;
  } catch {
    return EMPTY;
  }
}

// Per-storage-key snapshot cache so `getSnapshot` returns a referentially
// stable object between writes (required by useSyncExternalStore).
const snapshots = new Map<string, RepoSelectionHistory>();
const listeners = new Set<() => void>();

function notify() {
  for (const cb of listeners) cb();
}

function getSnapshot(key: string): RepoSelectionHistory {
  let snap = snapshots.get(key);
  if (!snap) {
    snap = readStore(key);
    snapshots.set(key, snap);
  }
  return snap;
}

function writeStore(key: string, data: RepoSelectionHistory) {
  snapshots.set(key, data);
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // A full or unavailable quota must not break picking a repo: the in-memory
    // snapshot still ranks this session, it just won't survive a reload.
  }
  // Same-tab subscribers share this module store, so notify them directly;
  // other tabs pick the change up through the native `storage` event.
  notify();
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === null) {
      snapshots.clear();
      notify();
    } else if (e.key.startsWith(`${STORAGE_KEY_PREFIX}:`)) {
      snapshots.delete(e.key);
      notify();
    }
  });
}

function subscribe(onStoreChange: () => void) {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

/** Fold one pick at `at` into `history`, applying both caps. Pure. */
function withSelection(
  history: RepoSelectionHistory,
  fullName: string,
  at: number,
): RepoSelectionHistory {
  const prev = history[fullName];
  const next: Record<string, RepoSelectionStats> = {
    ...history,
    [fullName]: {
      count: (prev?.count ?? 0) + 1,
      lastSelectedAt: at,
      recentSelections: [at, ...(prev?.recentSelections ?? [])].slice(0, MAX_SELECTIONS_PER_REPO),
    },
  };

  const names = Object.keys(next);
  if (names.length <= MAX_TRACKED_REPOS) return next;
  // Evict the coldest entries: least-recently picked first. The repo just
  // picked carries `at`, so it can never be the one dropped.
  const keep = names
    .sort((a, b) => next[b].lastSelectedAt - next[a].lastSelectedAt)
    .slice(0, MAX_TRACKED_REPOS);
  return Object.fromEntries(keep.map((name) => [name, next[name]]));
}

/**
 * The active org's repo pick history plus the recorder every picker calls when
 * a repo is chosen. Feeds `rankRepos` in `useGitHubRepos`, which is what
 * actually orders the rows.
 */
export function useRepoSelectionHistory() {
  const key = repoSelectionHistoryStorageKey(useOrgKey());
  const history = useSyncExternalStore(
    subscribe,
    () => getSnapshot(key),
    () => getSnapshot(key),
  );

  const recordSelection = useCallback(
    (fullName: string) => {
      if (!fullName) return;
      writeStore(key, withSelection(getSnapshot(key), fullName, Date.now()));
    },
    [key],
  );

  return { history, recordSelection };
}
